import axios, { type AxiosError } from 'axios';
import { app } from 'infra/configs/app';
import { buildClientContext } from 'infra/services/surface/buildClientContext';
import { getApiOverride } from '../api-switch';
import { responseCodeException } from '../exception';

export const CLIENT_CONTEXT_HEADER = {
	SURFACE: 'X-PX-Surface',
	NATIVE_APP: 'X-PX-Native-App',
	VIEWPORT_WIDTH: 'X-PX-Viewport-Width',
} as const;

const getHost = () => {
	const override = getApiOverride();
	return override || app.host;
};

const getBaseUrl = () => `${getHost()}/api`;

const api = axios.create({
	baseURL: getBaseUrl(),
	headers: {
		'Content-Type': 'application/json',
		Accept: 'application/json',
	},
});

api.interceptors.request.use((config) => {
	if (typeof window === 'undefined') return config;

	const { surface, is_native_app, viewport_width } = buildClientContext();

	config.headers.set(CLIENT_CONTEXT_HEADER.SURFACE, surface);
	config.headers.set(CLIENT_CONTEXT_HEADER.NATIVE_APP, String(is_native_app));

	if (viewport_width !== undefined) {
		config.headers.set(
			CLIENT_CONTEXT_HEADER.VIEWPORT_WIDTH,
			String(viewport_width),
		);
	}

	return config;
});

api.interceptors.response.use(
	(res) => res,
	(error: AxiosError<{ code?: string }>) => {
		responseCodeException(error.response?.data?.code);
		return Promise.reject(error);
	},
);

const setToken = (token: string) => {
	api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

const getFetcher = (url = '') => {
	api.defaults.baseURL = url;
	return api;
};

const getRootApi = () => getFetcher(getBaseUrl());

const getRootApiWithVersion = () =>
	getFetcher(`${getBaseUrl()}/v1/company-watcher`);

const getCompanyApi = () => getFetcher(`${getBaseUrl()}/v2/company`);

export {
	api,
	setToken,
	getHost,
	getRootApi,
	getCompanyApi,
	getRootApiWithVersion,
};
